import React, { Component } from 'react';
import { array, bool, func, oneOf, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import { NO_ACCESS_PAGE_USER_PENDING_APPROVAL, parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import { isErrorUserPendingApproval, isForbiddenError } from '../../util/errors';
import { isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { CollapseFAQ, H3, H5, NamedLink, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import { searchTypes } from "../../util/searchHelpers";

import css from './SearchPage.module.css';
import SearchPageHero from './Sections/SearchPageHero/SearchPageHero';
import SectionSchedule from './Sections/SectionSchedule/SectionSchedule';
import SectionFindCaringPetsitters from './Sections/SectionFindCaringPetsitters/SectionFindCaringPetsitters';
import SectionReasons from './Sections/SectionReasons/SectionReasons';
import SectionSupport from './Sections/SectionSupport/SectionSupport';
import SectionAdvice from './Sections/SectionAdvice/SectionAdvice';
import SectionWatchVideo from './Sections/SectionWatchVideo/SectionWatchVideo';
import SectionBlogPosts from './Sections/SectionBlogPosts/SectionBlogPosts';
import SectionFaq from './Sections/SectionFaq/SectionFaq';
import SectionFeedback from './Sections/SectionFeedback/SectionFeedback';
import SectionWhatIsHouseSitter from './Sections/SectionWhatIsHouseSitter/SectionWhatIsHouseSitter';
import SectionTipsHouseSitter from './Sections/SectionTipsHouseSitter/SectionTipsHouseSitter';
import SectionTipsHouseSit from './Sections/SectionTipsHouseSit/SectionTipsHouseSit';
import { getIsHouseSitter, userTypes } from '../../util/userTypes';
import { searchWithFilters } from './SearchPage.duck';
import SectionGrid421 from '../../components/StaticPageContainers/SectionGrid421';
import ContinueIcon from '../TopbarContainer/Topbar/images/continue-icon.svg';
import SectionGrid41 from '../../components/StaticPageContainers/SectionGrid41';
import SectionGrid31 from '../../components/StaticPageContainers/SectionGrid31';
import AvatarPlaceholder from './images/avatar_placeholder.png';
import SectionGrid21 from '../../components/StaticPageContainers/SectionGrid21';
import PartneringIcon from './images/partnering.png';
import VetIcon from './images/vet.png';
import SectionSimpleContainer from '../../components/StaticPageContainers/SectionSimpleContainer';
import { searchHouseSitFaq } from '../PetSittingServicesPage/sections/faqs';
import TrustWidgetSmall from '../../components/StaticPageContainers/TrustWidgetSmall';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchHouseSitPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchHouseSitPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          //  history.push(`/s/${searchTypes.houseSitBooking}?${searchQuery}`)
          history.push(createResourceLocatorString('SearchHouseSitPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchHouseSitPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }


  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      params,
      currentUser,
      onSearchWithFilters,
    } = this.props;

    const searchType = searchTypes.houseSitBooking;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter = true;//
    // searchParamsInURL.dates == null ||
    // (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
          ? listings.length
          : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      // searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      searchType,
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />

        <SearchPageHero
          searchType={searchType}
          history={this.props.history}
          config={this.props.config}
          currentSearchParams={validQueryParams}
          searchParamsInURL={searchParamsInURL}
          routeConfiguration={this.props.routeConfiguration}
          intl={intl}
          onSearchWithFilters={onSearchWithFilters}
          initialValuesForDatesSearch={initialValues(this.props, this.state.currentQueryParams)}
          liveEdit={true}
          getHandleChangedValueFn={this.getHandleChangedValueFn}
        />

        <div className={css.layoutWrapperContainer}>
          {/* <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersDesktop"
                    className={css.filter}
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                  />
                );
              })}
              <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
          </aside> */}

          <div className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>

              {/* <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;

                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersMobile"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile> */}
              {/* <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              /> */}
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  searchType={searchType}
                />
              </div>
            </div>
          </div>
        </div>

        {searchType === searchTypes.houseSitBooking
          ?
          <>
            {/* Newly added */}
            <SectionGrid421
              backgroundColor="white"
              header={
                <>
                  <p className={css.sectionTitle}>
                    Why HouseSit Match is Perfect for Sitters
                  </p>

                  <p className={css.info}>
                    HouseSit Match helps you explore new places, meet wonderful pets and enjoy comfortable homes around the world, all within a trusted, verified community.
                  </p>
                </>
              }
              content={
                <>
                  <div className={css.card}>
                    <H3>
                      Trusted, Verified Homeowners
                    </H3>
                    <p className={css.info}>
                      🔒 Every listing is posted by a verified homeowner, giving you peace of mind before you even apply.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      Free Accommodation Worldwide
                    </H3>
                    <p className={css.info}>
                      🌍 Enjoy rent-free stays in welcoming homes while caring for pets and discovering new destinations.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      A Supportive Global Community
                    </H3>
                    <p className={css.info}>
                      🤝 Connect with like-minded sitters who value kindness, reliability and care for animals.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      Help When You Need It
                    </H3>
                    <p className={css.info}>
                      🆘 Our UK-based support team and Vet Line access ensure you’re never on your own while house sitting.
                    </p>
                  </div>

                </>
              }
              footer={
                <>

                  <div className="col cenV fitWidth cenSelf gap20">
                    {/* <p className={css.info}>
                      ⭐ Rated Excellent on Trustpilot (4.8 / 5) • Verified members • UK-based support since 2013
                    </p> */}
                    <TrustWidgetSmall text="• Verified members • UK-based support since 2013" />

                    <NamedLink
                      className={css.ctaButton}
                      name="MembershipPlansPage"
                      params={{ accountType: userTypes.housesitter }}>
                      Join to Find a House Sit
                      <img src={ContinueIcon} className={css.icon} />
                    </NamedLink>
                  </div>
                </>
              } />


            <SectionGrid41
              header={
                <>
                  <p className={css.sectionTitle}>
                    How to Find a House Sit
                  </p>
                  <p className={css.info}>
                    Getting started as a house sitter is simple. Follow these easy steps to explore verified listings, connect with homeowners and start your next adventure with confidence.
                  </p>
                </>
              }
              content={
                <>
                  <div className={css.stepContainer}>
                    <H3>
                      <span class={css.circleNumber}>1</span>
                      Create Your Free Profile
                    </H3>
                    <p className={css.info}>
                      Register as a sitter and tell homeowners about your experience, pet care skills and travel preferences.
                    </p>
                  </div>
                  <div className={css.stepContainer}>
                    <H3>
                      <span class={css.circleNumber}>2</span>
                      Browse Verified Listings
                    </H3>
                    <p className={css.info}>
                      Use filters to explore available house sits in your preferred locations and dates.
                    </p>
                  </div>
                  <div className={css.stepContainer}>
                    <H3>
                      <span class={css.circleNumber}>3</span>
                      Apply and Connect
                    </H3>
                    <p className={css.info}>
                      Message homeowners directly through our secure platform to express interest and ask questions.
                    </p>
                  </div>
                  <div className={css.stepContainer}>
                    <H3>
                      <span class={css.circleNumber}>4</span>
                      Confirm and Enjoy Your Sit
                    </H3>
                    <p className={css.info}>
                      Once accepted, agree on details, pack your bags and enjoy free accommodation with trusted pets and hosts.
                    </p>
                  </div>
                </>
              }
              footer={
                <>
                  <div className="col cenV fitWidth cenSelf gap20">

                    <NamedLink
                      className={css.ctaButton}
                      name="SearchHouseSitPage">
                      Find Your Next House Sit
                      <img src={ContinueIcon} className={css.icon} />
                    </NamedLink>

                    <p className={css.info} style={{ fontSize: "12px" }}>
                      All sitters are ID-verified and reviewed by homeowners.
                    </p>

                  </div>
                </>
              } />

            <SectionGrid31
              backgroundColor="white"
              header={<>
                <p className={css.sectionTitle}>
                  What Our Members Say
                </p>

                <p className={css.info}>
                  Thousands of house and pet sitters have discovered new places, caring communities and unforgettable experiences through HouseSit Match. Here’s what a few of them shared.
                </p>
              </>}
              content={<>
                <div className={css.card}>
                  <img src={AvatarPlaceholder} className={css.reviewerImage} />

                  <p className={css.info}>
                    “This platform is like a family. The team is supportive, the homeowners are wonderful and every sit has been a great experience. I’ve used HouseSit Match for years and love it.”
                  </p>
                  <p className={css.reviewer}>
                    — Warren, House & Pet Sitter from the US
                  </p>

                </div>

                <div className={css.card}>
                  <img src={AvatarPlaceholder} className={css.reviewerImage} />

                  <p className={css.info}>
                    “I’ve travelled all over the UK and Europe thanks to HouseSit Match. It’s well organised, affordable and a great way to see the world while caring for pets.”
                  </p>
                  <p className={css.reviewer}>
                    — Kirsty, House Sitter from the UK
                  </p>

                </div>

                <div className={css.card}>
                  <img src={AvatarPlaceholder} className={css.reviewerImage} />

                  <p className={css.info}>
                    “The site makes it so easy to connect with homeowners. I’ve built lasting friendships and cared for pets in some amazing homes.”
                  </p>
                  <p className={css.reviewer}>
                    — Luc, House Sitter from France
                  </p>

                </div>
              </>}
              footer={
                <div className="col cenV fitWidth cenSelf gap20">
                  {/* ⭐ Trustpilot 4.8/5 from nearly 400 reviews • Google Reviews 4.7/5 from 35 reviews */}
                  <TrustWidgetSmall text="• Google Reviews 4.7/5 from 35 reviews " />

                  <a href="https://uk.trustpilot.com/review/housesitmatch.com" target="_blank" rel="noopener noreferrer">
                    Read more reviews on Trustpilot
                  </a>
                </div>
              } />

            <SectionGrid21
              header={<>
                <p className={css.sectionTitle}>
                  Giving Back and Supporting Pet Welfare
                </p>

                <p className={css.info}>
                  We care about pets in every home and beyond. That’s why HouseSit Match supports animal welfare charities and provides practical help to members through our 24/7 Vet Advice Line.
                </p>
              </>}
              content={<>
                <div className={css.stepContainer}>
                  <img src={PartneringIcon} className='cenSelf' />
                  <p className={css.subtitle}>
                    Partnering with Pet Charities
                  </p>
                  <p className={css.info}>
                    We donate 50% of affiliate revenue generated by our partner pet charities back to those organisations. Every new member helps fund animal rescue, rehoming and welfare projects across the UK and beyond.
                  </p>
                </div>

                <div className={css.stepContainer}>
                  <img src={VetIcon} className='cenSelf' />
                  <p className={css.subtitle}>
                    Free 24/7 Vet Advice Line
                  </p>
                  <p className={css.info}>
                    Active Choice and Premium members have free access to our 24/7 Vet Advice Line. Whether you’re on a sit in the UK or abroad, you can speak directly to qualified veterinary nurses for expert help and peace of mind.
                  </p>

                </div>
              </>} />

            <SectionGrid421
              backgroundColor="white"
              header={
                <>
                  <p className={css.sectionTitle}>
                    Tips for Getting the Best House Sitting Jobs
                  </p>

                  <p className={css.info}>
                    Whether you’re new to house sitting or looking to secure more sits, these simple tips can help you stand out and find the right opportunities for your lifestyle.
                  </p>
                </>
              }
              content={
                <>
                  <div className={css.card}>
                    <H3>
                      ✔️ Build a Strong Profile
                    </H3>
                    <p className={css.info}>
                      Highlight your experience, include a friendly photo, and mention any pet care or home management skills. A detailed profile helps homeowners trust you right away.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      ✔️ Apply Early and Often
                    </H3>
                    <p className={css.info}>
                      New listings are added daily, and the most popular sits get booked quickly. Check the site regularly and apply early to improve your chances.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      ✔️ Communicate Clearly
                    </H3>
                    <p className={css.info}>
                      When messaging homeowners, be friendly, professional and specific about your availability and experience. Clear communication builds confidence and better matches.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      ✔️ Collect Reviews After Each Sit
                    </H3>
                    <p className={css.info}>
                      Reviews show your reliability and care. Each successful sit strengthens your profile and helps you get chosen for future opportunities.
                    </p>
                  </div>

                </>
              }
              footer={
                <>

                  <div className="col cenV fitWidth cenSelf gap20">

                    <NamedLink
                      className={css.ctaButton}
                      name="SearchHouseSitPage"
                      params={{ accountType: userTypes.housesitter }}>
                      Start Searching for House Sits
                      <img src={ContinueIcon} className={css.icon} />
                    </NamedLink>
                  </div>
                </>
              } />

            <SectionSimpleContainer
              header={
                <>
                  <p className={css.sectionTitle}>
                    Start Your House Sitting Journey
                  </p>
                  <p className={css.info}>
                    Ready to start exploring new places and caring for pets around the world? Join our trusted community of house sitters and discover opportunities that match your lifestyle and travel goals.
                  </p>
                </>}
              footer={<>
                <div className="col cenV fitWidth cenSelf gap20">

                  <div className='rowMobile gap20'>
                    <NamedLink
                      className={css.ctaButton}
                      name="SignupPage">
                      Join to Find a House Sit
                      <img src={ContinueIcon} className={css.icon} />
                    </NamedLink>

                    <NamedLink
                      className={css.ctaButton}
                      name="MembershipPlansPage"
                      params={{ accountType: userTypes.housesitter }}>
                      View Membership Plans
                      <img src={ContinueIcon} className={css.icon} />
                    </NamedLink>
                  </div>

                  <p className={css.info} style={{ fontSize: "12px" }}>
                    Registration is free. Membership gives you full access to verified listings and secure messaging with homeowners.
                  </p>
                </div>
              </>
              }
            />

            <SectionSimpleContainer
              backgroundColor="white"
              header={
                <p className={css.sectionTitle}>
                  FAQs About House Sitting Jobs
                </p>
              }
              content={
                <CollapseFAQ
                  list={searchHouseSitFaq}
                />
              } />

          </>
          :
          <>
            {/* Newly added */}
            <SectionGrid421
              backgroundColor="white"
              header={
                <>
                  <p className={css.sectionTitle}>
                    Why Choose HouseSit Match Sitters
                  </p>

                  <p className={css.info}>
                    We make finding the right sitter simple and safe. Our members are carefully verified, supported by our UK-based team and trusted by homeowners around the world.
                  </p>
                </>
              }
              content={
                <>
                  <div className={css.card}>
                    <H3>
                      Verified and Reviewed Sitters
                    </H3>
                    <p className={css.info}>
                      🔒 Every sitter completes ID checks and collects verified reviews from homeowners after each assignment.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      Pet-Care Specialists
                    </H3>
                    <p className={css.info}>
                      🤝 Many of our sitters have experience caring for
                      <NamedLink
                        className={css.link}
                        name="DogSittingPageRedirect">
                        dogs
                      </NamedLink>,
                      <NamedLink
                        className={css.link}
                        name="CatsSittingPageRedirect">
                        cats
                      </NamedLink> and small animals, so your pets stay happy and comfortable at home.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      Trusted UK and Global Network
                    </H3>
                    <p className={css.info}>
                      🌍 Thousands of active members connect through HouseSit Match across the UK and internationally, ensuring great matches anywhere you need care.
                    </p>
                  </div>
                  <div className={css.card}>
                    <H3>
                      Support When You Need It
                    </H3>
                    <p className={css.info}>
                      🆘 Our family-run UK team is always on hand with guidance and technical help throughout your membership.
                    </p>
                  </div>

                </>
              }
              footer={
                <>

                  <div className="col cenV fitWidth cenSelf gap20">
                    <p className={css.info}>
                      ⭐ Rated Excellent on Trustpilot (4.8 / 5) • Verified members • UK-based support since 2013
                    </p>

                    <NamedLink
                      className={css.ctaButton}
                      name="MembershipPlansPage"
                      params={{ accountType: userTypes.homeowner }}>
                      List Your House Sitting Opportunity
                      <img src={ContinueIcon} className={css.icon} />
                    </NamedLink>
                  </div>
                </>
              } />

            {/* {
              searchType === searchTypes.houseSitter &&
              <SectionWhatIsHouseSitter />
            }
            {
              searchType === searchTypes.houseSitBooking &&
              <SectionReasons />
            }
            <SectionSchedule searchType={searchType} />
            <SectionFindCaringPetsitters searchType={searchType} />
            <SectionSupport searchType={searchType} />
            <SectionAdvice searchType={searchType} />
            <SectionWatchVideo searchType={searchType} />

            {
              searchType === searchTypes.houseSitter &&
              <SectionTipsHouseSitter />
            }

            {
              searchType === searchTypes.houseSitBooking &&
              <SectionTipsHouseSit />
            }
            <SectionFaq searchType={searchType} />
            <SectionFeedback />

            {
              searchType === searchTypes.houseSitBooking &&
              <SectionBlogPosts />
            } */}
          </>
        }

        <FooterContainer />
      </Page>
    );
  }
}

SearchHouseSitPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
};

SearchHouseSitPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchHouseSitPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  }

  return (
    <SearchHouseSitPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchHouseSitPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onSearchWithFilters: (params, config) => dispatch(searchWithFilters(params, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchHouseSitPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchHouseSitPage);

export default SearchHouseSitPage;
